import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "../lib/api";

const AuthContext = createContext(null);

const PREVIEW_STORAGE_KEY = "dwg_view_as";

const readPreview = () => {
  try {
    const raw = sessionStorage.getItem(PREVIEW_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};
const writePreview = (v) => {
  try {
    if (v) sessionStorage.setItem(PREVIEW_STORAGE_KEY, JSON.stringify(v));
    else sessionStorage.removeItem(PREVIEW_STORAGE_KEY);
  } catch {}
};

export const AuthProvider = ({ children }) => {
  const [realUser, setRealUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewAs, setViewAs] = useState(() => readPreview());

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setRealUser(data);
    } catch {
      setRealUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.access_token) localStorage.setItem("dwg_token", data.access_token);
    setRealUser(data.user);
    return data.user;
  };

  const loginWithSession = (u) => setRealUser(u);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { console.warn("logout API failed (session cleared locally):", e?.message); }
    localStorage.removeItem("dwg_token");
    writePreview(null);
    setViewAs(null);
    setRealUser(false);
  };

  // ---- Role Preview (Owner-only simulated view) ----
  const enterPreview = useCallback((preview) => {
    // preview = { role: string, division_id: string|null, label: string }
    if (!realUser || realUser.role !== "owner") return;
    writePreview(preview);
    setViewAs(preview);
  }, [realUser]);

  const exitPreview = useCallback(() => {
    writePreview(null);
    setViewAs(null);
  }, []);

  // effectiveUser: if preview aktif & realUser is owner, overlay role/division_id
  // Semua consumer (Layout, Dashboard, Guard, halaman) baca `user` — otomatis follow preview
  const effectiveUser = useMemo(() => {
    if (!realUser || realUser === false) return realUser;
    if (!viewAs) return realUser;
    if (realUser.role !== "owner") return realUser; // safety: hanya owner boleh preview
    return {
      ...realUser,
      role: viewAs.role,
      division_id: viewAs.division_id || null,
      _preview: true, // marker internal
    };
  }, [realUser, viewAs]);

  return (
    <AuthContext.Provider value={{
      user: effectiveUser, realUser, viewAs, loading,
      login, loginWithSession, logout, refresh,
      enterPreview, exitPreview,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
